import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";
import { useTrackCreation } from "../../../../context/TrackCreationContext";
import Button from "../../../../components/ui/Button";
import AddButton from "../../../../components/ui/AddButton";
import DeleteModal from "../../../../components/modals/DeleteModal";
import { getWaypointsForTrack, deleteWaypoint as deleteWaypointAPI } from "../../../../api/waypoint";
import { applyUpdatesToTrack } from "../../../../api/track";
import i18n from "../../../../i18n";
import styles from "./ManageContentPlaylist.module.css";
import Icon from "../../../../components/common/Icon";
import stepStyles from "../createStep.module.css";
import pageHeader from "../../../../components/layouts/PageHeader.module.css";

interface ManageContentPlaylistProps {
    navigateToNext: () => void;
}

interface WaypointPicture {
    id?: string;
    url: string;
}

interface LocalWaypoint {
    id: string;
    internalId?: string;
    position: number;
    status?: boolean;
    triggerMode?: string;
    type?: string;
    pictures?: WaypointPicture[] | string[];
    waypointLocalized?: Array<{
        name: string;
        language: string;
    }>;
    TemporaryWaypointLocalized?: Array<{
        title: string;
        language: string;
    }>;
    isTemporary?: boolean;
}

const ManageContentPlaylist: React.FC<ManageContentPlaylistProps> = ({ navigateToNext }) => {
    const { trackData } = useTrackCreation();
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const navigate = useNavigate();
    const token = localStorage.getItem("token") || "";

    const [waypoints, setWaypoints] = useState<LocalWaypoint[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [storyToDelete, setStoryToDelete] = useState<LocalWaypoint | null>(null);
    const [publishing, setPublishing] = useState<boolean>(false);

    const linkId = trackData?.trackId || trackData?.id;

    const loadData = async () => {
        if (!trackData.trackId) return;

        try {
            setLoading(true);

            const waypointsResponse = await getWaypointsForTrack({
                token,
                trackId: trackData.trackId,
            });

            // Merge definitive waypoints and temporary waypoints
            const temporaryWaypoints: LocalWaypoint[] = (waypointsResponse?.temporaryWaypoints || []).map(
                (wpt: any) => ({
                    ...wpt,
                    id: wpt.id || wpt.internalId,
                    isTemporary: true,
                }),
            );

            // Get the IDs of definitive waypoints that have a corresponding temporary waypoint
            const definitiveIdsWithDrafts = new Set(
                temporaryWaypoints.filter((wpt: any) => wpt.waypointId).map((wpt: any) => wpt.waypointId),
            );

            // Filter out definitive waypoints that have a pending draft
            const definitiveWaypoints: LocalWaypoint[] = (waypointsResponse?.waypoints || [])
                .filter((wpt: any) => !definitiveIdsWithDrafts.has(wpt.id))
                .map((wpt: any) => ({
                    ...wpt,
                    isTemporary: false,
                }));

            // Combine, filter to MANUAL only, and sort by position
            const allWaypoints = [...definitiveWaypoints, ...temporaryWaypoints]
                .filter((wpt) => wpt.triggerMode === "MANUAL" && wpt.type === "INVISIBLE")
                .sort((a, b) => (a.position || 0) - (b.position || 0));

            setWaypoints(allWaypoints);
        } catch (error) {
            console.error("Error loading data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trackData.trackId, token]);

    const getWaypointLocalizedName = (wpt: LocalWaypoint) => {
        const langCode = i18n.language?.toUpperCase();

        if (wpt.waypointLocalized && wpt.waypointLocalized.length > 0) {
            const localized = wpt.waypointLocalized.find((loc) => loc.language?.toUpperCase() === langCode);
            if (localized?.name) return localized.name;
            if (wpt.waypointLocalized[0]?.name) return wpt.waypointLocalized[0].name;
        }

        if (wpt.TemporaryWaypointLocalized && wpt.TemporaryWaypointLocalized.length > 0) {
            const localized = wpt.TemporaryWaypointLocalized.find((loc) => loc.language?.toUpperCase() === langCode);
            if (localized?.title) return localized.title;
            if (wpt.TemporaryWaypointLocalized[0]?.title) return wpt.TemporaryWaypointLocalized[0].title;
        }

        return t("story") + " " + wpt.position;
    };

    const handleCreate = () => {
        navigate(`/create/tour/${linkId}/content-playlist/create`);
    };

    const handleEdit = (storyId: string) => {
        navigate(`/create/tour/${linkId}/content-playlist/modify/${storyId}`);
    };

    const handleDelete = async () => {
        if (!storyToDelete) return;

        const deletedStory = storyToDelete;
        setWaypoints((prev) => prev.filter((wpt) => wpt.id !== deletedStory.id));
        setDeleteModalOpen(false);
        setStoryToDelete(null);

        try {
            await deleteWaypointAPI({ token, waypointId: deletedStory.id, trackId: trackData.trackId || "" });
        } catch (error) {
            console.error("Error deleting story:", error);
            setWaypoints((prev) => [...prev, deletedStory].sort((a, b) => a.position - b.position));
        }
    };

    const handlePublishAll = async () => {
        if (!trackData.trackId) return;

        setPublishing(true);
        try {
            await applyUpdatesToTrack({ token, trackId: trackData.trackId, triggerModeFilter: "MANUAL" });
            await loadData();
        } catch (error) {
            console.error("Error publishing stories:", error);
        } finally {
            setPublishing(false);
        }
    };

    const hasTemporaryStories = waypoints.some((wpt) => wpt.isTemporary);

    const getFirstPicture = (wpt: LocalWaypoint) => {
        if (wpt.pictures && wpt.pictures.length > 0) {
            return typeof wpt.pictures[0] === "string" ? wpt.pictures[0] : (wpt.pictures[0] as any)?.url;
        }
        return null;
    };

    return (
        <div className={styles.container}>
            {/* Header */}
            <div className={pageHeader.withBreadcrumb}>
                <div className={pageHeader.titleSection}>
                    <div className={pageHeader.content}>
                        <h2 className="settings-title">{t("manageContentPlaylistTitle")}</h2>
                        <p className={pageHeader.subtitle}>{t("manageContentPlaylistSubtitle")}</p>
                    </div>
                    <div className={styles.headerActions}>
                        <AddButton onClick={handleCreate} tooltip={t("createContentPlaylistItem")} />
                    </div>
                </div>
            </div>

            {/* Content */}
            {loading ? (
                <div className={styles.loading}>{t("loading")}</div>
            ) : waypoints.length === 0 ? (
                <div className={styles.emptyState}>
                    <h3 className={styles.emptyStateTitle}>{t("noStoriesYet")}</h3>
                    <p className={styles.emptyStateText}>{t("noStoriesDescription")}</p>
                    <AddButton onClick={handleCreate} tooltip={t("createContentPlaylistItem")} />
                </div>
            ) : (
                <div className={styles.storiesGrid}>
                    {waypoints.map((wpt) => {
                        const firstPic = getFirstPicture(wpt);
                        return (
                            <div
                                key={wpt.id || wpt.internalId}
                                className={styles.storyCard}
                                onClick={() => handleEdit(wpt.id)}
                            >
                                {wpt.isTemporary && (
                                    <Tooltip title={t("draftWillBecomeLiveWhenPublished")} arrow>
                                        <div className={styles.draftBadge}>{t("draft")}</div>
                                    </Tooltip>
                                )}

                                <div className={styles.storyCardHeader}>
                                    <div className={styles.storyImage}>
                                        {firstPic ? (
                                            <img src={firstPic} alt={getWaypointLocalizedName(wpt)} />
                                        ) : (
                                            <Icon name="ic-image" className={styles.storyImagePlaceholder} />
                                        )}
                                    </div>
                                    <div className={styles.storyInfo}>
                                        <div className={styles.storyNameRow}>
                                            <h4 className={styles.storyName}>{getWaypointLocalizedName(wpt)}</h4>
                                        </div>
                                        <div className={styles.storyMeta}>
                                            <span className={styles.metaItem}>
                                                {t("position")}: {wpt.position}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className={styles.storyCardActions}>
                                    <div className={styles.actionButtons}>
                                        <Tooltip title={t("edit")} arrow>
                                            <button
                                                type="button"
                                                className={styles.actionIcon}
                                                onClick={(e: React.MouseEvent) => {
                                                    e.stopPropagation();
                                                    handleEdit(wpt.id);
                                                }}
                                                aria-label={t("edit")}
                                            >
                                                <Icon name="ic-edit" size={18} />
                                            </button>
                                        </Tooltip>
                                        <Tooltip title={t("delete")} arrow>
                                            <button
                                                type="button"
                                                className={styles.actionIconDanger}
                                                onClick={(e: React.MouseEvent) => {
                                                    e.stopPropagation();
                                                    setStoryToDelete(wpt);
                                                    setDeleteModalOpen(true);
                                                }}
                                                aria-label={t("delete")}
                                            >
                                                <Icon name="ic-trash" size={18} />
                                            </button>
                                        </Tooltip>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Action buttons — mock .foot */}
            <div className={`${stepStyles.stepButton} ${stepStyles.footer}`}>
                {hasTemporaryStories && (
                    <Button
                        label={publishing ? t("publishing") : t("publishAllStories")}
                        onClick={handlePublishAll}
                        disabled={publishing || !trackData.trackId}
                        variant="outline"
                        shape="pill"
                        className={stepStyles.footerBtn}
                    />
                )}
                <Button
                    label={t("continue")}
                    onClick={navigateToNext}
                    variant="primary"
                    shape="pill"
                    className={stepStyles.continueBtn}
                />
            </div>

            {/* Delete confirmation modal */}
            {storyToDelete && (
                <DeleteModal
                    isOpen={deleteModalOpen}
                    onClose={() => {
                        setDeleteModalOpen(false);
                        setStoryToDelete(null);
                    }}
                    onConfirm={handleDelete}
                    message={t("deleteStoryConfirmation")}
                    title={t("deleteContentPlaylistItem")}
                />
            )}
        </div>
    );
};

export default ManageContentPlaylist;
